import React, { useState, useEffect } from 'react';
import { getOrganizationDisplayName } from '@common/organizationDisplayName';
import type { Organization, GlobalAsset, EnterpriseFsatResult } from '@types';
import { ICONS } from './constants';
import { getFunctions, httpsCallable } from 'firebase/functions';

interface EnterpriseFsatEngineProps {
    isOpen: boolean;
    onClose: () => void;
    organization: Organization;
    assets: GlobalAsset[];
    onComplete: (result: EnterpriseFsatResult) => void;
}

export const EnterpriseFsatEngine: React.FC<EnterpriseFsatEngineProps> = ({ isOpen, onClose, organization, assets, onComplete }) => {
    const [progress, setProgress] = useState(0);
    const [loadingText, setLoadingText] = useState('Initializing Engine Core...');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        if (!isOpen) {
            setProgress(0);
            setErrorMessage(null);
            return;
        }

        let isMounted = true;

        const runSweep = async () => {
            try {
                setErrorMessage(null);
                const validAssets = assets.filter(asset => asset.name?.trim() && asset.type && asset.criticality);
                if (!validAssets.length)
                    throw new Error('Add at least one asset with a name, type, and criticality before starting an FSAT sweep.');

                // Step 1: Initializing
                if (isMounted) setLoadingText(`Authenticating Organization Profile: ${getOrganizationDisplayName(organization)}`);
                await new Promise(r => setTimeout(r, 400));
                if (isMounted) setProgress(20);

                // Step 2: Asset retrieval
                if (isMounted) setLoadingText(`Retrieving ${assets.length} Global Physical Assets...`);
                await new Promise(r => setTimeout(r, 600));
                if (isMounted) setProgress(45);

                // Step 3: API Call Simulation
                if (isMounted) setLoadingText('Cross-referencing Asset footprints against live Geopolitical Streams (P1) and Resilience Frameworks (P3)...');
                
                // Actual API call
                const functions = getFunctions();
                const generateEnterpriseFsatSweep = httpsCallable(functions, 'generateEnterpriseFsatSweep');
                const response = await generateEnterpriseFsatSweep({
                    organizationId: organization.id,
                    industry: organization.industry?.trim() || 'Unspecified Industry',
                    assets: validAssets
                });
                const result = response.data as EnterpriseFsatResult;
                
                if (isMounted) {
                    setProgress(85);
                    setLoadingText('Synthesizing Enterprise FSAT output payload...');
                    await new Promise(r => setTimeout(r, 500));
                    setProgress(100);
                    setTimeout(() => {
                        onComplete(result);
                    }, 400);
                }
            } catch (error) {
                console.error("FSAT Engine Error", error);
                if (isMounted) {
                    const message = error instanceof Error && error.message
                        ? error.message.replace(/^FirebaseError:\s*/i, '')
                        : 'FSAT sweep failed. Please try again.';
                    setErrorMessage(message);
                    setLoadingText('FSAT sweep stopped');
                }
            }
        };

        runSweep();

        return () => { isMounted = false; };
    }, [isOpen, organization, assets, onComplete]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center animate-fade-in p-4">
            <div className="absolute inset-0 bg-slate-900/90 backdrop-blur-xl" />
            
            <div className="relative w-full max-w-3xl glass-panel p-10 flex flex-col items-center justify-center transform transition-transform animate-scale-in text-center shadow-[0_0_100px_rgba(59,130,246,0.3)] border border-blue-500/50 rounded-2xl">
                
                <div className="mb-8 relative">
                    <div className="absolute inset-0 bg-blue-500/20 blur-[50px] rounded-full animate-pulse" />
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" className="w-32 h-32 text-blue-500 relative z-10 animate-[spin_4s_linear_infinite]">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418" />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center z-20 text-white">
                        {ICONS.shield}
                    </div>
                </div>

                <h2 className="text-3xl font-black text-white tracking-widest uppercase mb-4 shadow-black drop-shadow-lg">
                    Enterprise FSAT Sweep
                </h2>

                <p className="text-blue-400 font-mono text-sm tracking-widest mb-10 h-6">
                    {loadingText}
                </p>

                {errorMessage && (
                    <div className="w-full mb-6 rounded-xl border border-red-500/50 bg-red-950/50 p-4 text-left">
                        <p className="text-sm font-semibold text-red-200">{errorMessage}</p>
                    </div>
                )}

                <div className="w-full bg-slate-800 rounded-full h-2 mb-4 overflow-hidden border border-slate-700">
                    <div 
                        className="bg-gradient-to-r from-blue-600 to-indigo-400 h-2 rounded-full transition-all duration-500 ease-out relative" 
                        style={{ width: `${progress}%` }}
                    >
                        <div className="absolute top-0 right-0 bottom-0 w-20 bg-white/30 blur-[4px]" />
                    </div>
                </div>

                <div className="flex justify-between w-full text-slate-500 font-mono text-xs uppercase tracking-widest">
                    <span>{progress}% Completed</span>
                    <span>{errorMessage ? 'Action Required' : 'System Online'}</span>
                </div>

                {errorMessage && (
                    <button
                        type="button"
                        onClick={onClose}
                        className="mt-6 rounded-lg bg-slate-700 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-slate-600"
                    >
                        Close
                    </button>
                )}
            </div>
        </div>
    );
};
